import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { PushSendResult, PushTarget } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Chips, Dot, Empty, ErrorState, Field } from "../components/Ui.tsx";

/**
 * Bildirim kampanyaları.
 *
 * Akış bilinçli olarak üç adımlı: hedef kitleyi TAHMİN et → kendine TEST gönder →
 * TOTP ile toplu gönder. Gönderilen bildirim geri çekilemez; tek koruma
 * göndermeden önce ne olduğunu görmek.
 */

const APP_KEY = "gwrctl.notifications.app";
const TEST_IDS_KEY = "gwrctl.notifications.testIds";
const TESTED_KEY = "gwrctl.notifications.tested";

// Depolama gizli pencerede ya da kapalıyken hata fırlatabilir; tercih kaybı sorun değil.
function readStore(storage: "local" | "session", key: string): string | null {
  try {
    return (storage === "local" ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}
function writeStore(storage: "local" | "session", key: string, value: string): void {
  try {
    (storage === "local" ? localStorage : sessionStorage).setItem(key, value);
  } catch {
    /* tercih hatırlanmaz, o kadar */
  }
}

const LOCALES = ["all", "tr", "en"] as const;
const LOCALE_LABELS = { all: "tüm diller", tr: "Türkçe", en: "İngilizce" };
const PLANS = ["all", "premium", "free"] as const;
const PLAN_LABELS = { all: "herkes", premium: "premium", free: "ücretsiz" };
const ACTIVITY = ["all", "inactive30"] as const;
const ACTIVITY_LABELS = { all: "herkes", inactive30: "30 gündür girmeyen" };

/** Test alıcıları virgül, boşluk ya da satırla ayrılabilir — yapıştırmak kolay olsun. */
function parseIds(raw: string): string[] {
  return [...new Set(raw.split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean))];
}

/** Bu içerik bu oturumda test edildi mi — başlık/metin değişince test sayılmaz. */
function contentKey(appId: string, title: string, body: string): string {
  return `${appId}\u0000${title}\u0000${body}`;
}

export function NotificationsPage(): ReactNode {
  const toast = useToast();
  const [pickedApp, setPickedApp] = useState<string | null>(() => readStore("local", APP_KEY));
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState<PushTarget>({});
  const [totp, setTotp] = useState("");
  const [testIds, setTestIdsRaw] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(readStore("local", TEST_IDS_KEY) ?? "{}") as Record<string, string>;
    } catch {
      return {};
    }
  });
  const [tested, setTested] = useState<string[]>(() => {
    try {
      return JSON.parse(readStore("session", TESTED_KEY) ?? "[]") as string[];
    } catch {
      return [];
    }
  });
  const [result, setResult] = useState<PushSendResult | null>(null);

  const apps = useQuery({ queryKey: ["apps"], queryFn: () => client.apps() });
  // Cihaz kaydı (Expo push token) yapan uygulamalar "notifications" panelini ilan eder. OTA yeteneğine
  // bakılmaz: DailyGuess'in OTA'sı kendi sunucusunda, ama push kaydı var.
  const pushApps = apps.data?.filter((a) => a.panels.includes("notifications")) ?? [];
  // Hatırlanan seçim artık listede yoksa ilk uygun uygulamaya düş; sabit "movieport" yok.
  const appId = pushApps.find((a) => a.id === pickedApp)?.id ?? pushApps[0]?.id ?? "";

  const selectApp = (id: string): void => {
    setPickedApp(id);
    setResult(null);
    writeStore("local", APP_KEY, id);
  };

  const rawTestIds = testIds[appId] ?? "";
  const setTestIds = (value: string): void => {
    const next = { ...testIds, [appId]: value };
    setTestIdsRaw(next);
    writeStore("local", TEST_IDS_KEY, JSON.stringify(next));
  };
  const testList = parseIds(rawTestIds);

  const stats = useQuery({
    queryKey: ["device-stats", appId],
    queryFn: () => client.deviceStats(appId),
    enabled: Boolean(appId),
  });
  const campaigns = useQuery({
    queryKey: ["campaigns", appId],
    queryFn: () => client.pushCampaigns(appId),
    enabled: Boolean(appId),
  });
  const estimate = useQuery({
    queryKey: ["push-estimate", appId, target],
    queryFn: () => client.pushEstimate(appId, target),
    enabled: Boolean(appId),
  });

  const send = useMutation({
    mutationFn: async (testOnly: boolean) => {
      if (!testOnly) {
        if (!/^\d{6}$/.test(totp)) throw new Error("Toplu gönderim için 6 haneli doğrulama kodu gerekli");
        await client.confirm(totp);
      }
      // Test gönderimi kampanya hedefini KULLANMAZ; yalnızca elle girilen
      // kimliklere gider. Sunucu da bunu ayrıca zorunlu kılıyor.
      const sendTarget = testOnly ? { userIds: testList } : target;
      return client.pushSend({ appId, title, body, target: sendTarget, testOnly });
    },
    onSuccess: (res, testOnly) => {
      setResult(res);
      setTotp("");
      void campaigns.refetch();
      if (testOnly && res.sent > 0) {
        const next = [...new Set([...tested, contentKey(appId, title, body)])];
        setTested(next);
        writeStore("session", TESTED_KEY, JSON.stringify(next));
      }
      toast({
        tone: res.failed ? "danger" : "ok",
        title: testOnly ? "Test gönderildi" : "Bildirim gönderildi",
        body: `${res.sent} gönderildi · ${res.failed} başarısız`,
      });
    },
  });

  const locale = (target.locale ?? "all") as (typeof LOCALES)[number];
  const plan = (target.plan ?? "all") as (typeof PLANS)[number];
  const activity = target.inactiveDays === 30 ? "inactive30" : "all";

  const audienceParts = [
    plan === "all" ? null : PLAN_LABELS[plan],
    activity === "all" ? null : ACTIVITY_LABELS[activity],
  ].filter(Boolean);
  const audienceLabel = audienceParts.length ? audienceParts.join(" + ") : "tüm kullanıcılar";
  const countLabel = estimate.isError
    ? "? kişi"
    : estimate.data
      ? `${estimate.data.count.toLocaleString("tr-TR")} kişi`
      : "… kişi";
  const summary = `${countLabel} · ${audienceLabel} · ${LOCALE_LABELS[locale]}`;

  const wasTested = tested.includes(contentKey(appId, title, body));
  const ready = Boolean(appId && title.trim() && body.trim());

  return (
    <>
      <PageHeader kicker="yönetim" title="Bildirimler" />

      <PageBody>
        {apps.isError ? (
          <Card>
            <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
          </Card>
        ) : apps.isLoading ? (
          <Card>
            <Empty>Uygulamalar yükleniyor…</Empty>
          </Card>
        ) : !pushApps.length ? (
          <Card>
            <Empty>Bildirim gönderebilen bir uygulama yok.</Empty>
          </Card>
        ) : (
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <div className="grid min-w-0 gap-4">
              <Card title="Kampanya" hint="tahmin et → kendine test → toplu gönder" flush>
                <div className="grid gap-3.5 p-4">
                  <Field
                    label="Uygulama"
                    hint="Yalnızca bildirim altyapısı kurulu uygulamalar listelenir."
                  >
                    <Chips
                      values={pushApps.map((a) => a.id)}
                      value={appId}
                      onChange={selectApp}
                      labels={Object.fromEntries(pushApps.map((a) => [a.id, a.displayName]))}
                    />
                  </Field>

                  <Field label="Başlık · en çok 100 karakter">
                    <input
                      className="input"
                      value={title}
                      maxLength={100}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </Field>

                  <Field label="Metin · en çok 300 karakter">
                    <textarea
                      className="input"
                      rows={3}
                      value={body}
                      maxLength={300}
                      onChange={(e) => setBody(e.target.value)}
                    />
                  </Field>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Dil">
                      <Chips
                        values={LOCALES}
                        value={locale}
                        labels={LOCALE_LABELS}
                        onChange={(v) => setTarget({ ...target, locale: v === "all" ? undefined : v })}
                      />
                    </Field>
                    <Field label="Abonelik">
                      <Chips
                        values={PLANS}
                        value={plan}
                        labels={PLAN_LABELS}
                        onChange={(v) => setTarget({ ...target, plan: v === "all" ? undefined : v })}
                      />
                    </Field>
                    <Field label="Etkinlik">
                      <Chips
                        values={ACTIVITY}
                        value={activity}
                        labels={ACTIVITY_LABELS}
                        onChange={(v) =>
                          setTarget({ ...target, inactiveDays: v === "inactive30" ? 30 : undefined })
                        }
                      />
                    </Field>
                  </div>

                  <div className="flex flex-wrap items-baseline gap-2.5 rounded-[8px] border border-neutral-800 bg-white/[0.02] px-3.5 py-3">
                    <span className="text-xs text-neutral-400">Hedef kitle</span>
                    {estimate.isError ? (
                      <>
                        <span className="text-sm text-danger">
                          {humanizeError(estimate.error, "Tahmin alınamadı")}
                        </span>
                        <Button size="sm" className="ml-auto" onClick={() => void estimate.refetch()}>
                          Tekrar dene
                        </Button>
                      </>
                    ) : (
                      <>
                        <span
                          className="mono text-lg font-medium text-accent-300"
                          style={{ fontFamily: "var(--font-heading)" }}
                          aria-busy={estimate.isFetching || undefined}
                        >
                          {estimate.data ? estimate.data.count.toLocaleString("tr-TR") : "…"}
                        </span>
                        <span className="text-xs text-neutral-500">cihaz</span>
                      </>
                    )}
                  </div>

                  <Field
                    label="Test alıcıları"
                    hint={
                      <>
                        Test yalnızca bu kullanıcılara gider; yukarıdaki hedefleme uygulanmaz. Kimliği
                        uygulamanın Supabase panelinde Authentication → Users listesinden kopyala
                        (virgül, boşluk ya da satırla birden fazla). Bu tarayıcıda uygulama başına
                        hatırlanır.
                        {testList.length > 0 && ` · ${testList.length} alıcı`}
                      </>
                    }
                  >
                    <input
                      className="input mono"
                      value={rawTestIds}
                      onChange={(e) => setTestIds(e.target.value)}
                      placeholder="ör. 3f2a…-…  (kendi kullanıcı kimliğin)"
                    />
                  </Field>

                  <Banner tone="warn" kicker="geri alınamaz">
                    Gönderilen bildirim geri çekilemez. Tek koruma: önce kendine test gönder.
                  </Banner>

                  {ready && !wasTested && (
                    <Banner tone="info" kicker="test yok">
                      Bu başlık ve metinle bu oturumda test gönderimi yapılmadı. Toplu göndermeden önce
                      kendine bir test at.
                    </Banner>
                  )}

                  <p className="text-sm text-neutral-200" aria-live="polite">
                    <span className="text-neutral-500">Gönderilecek: </span>
                    {summary}
                  </p>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      disabled={!ready || !testList.length || send.isPending}
                      loading={send.isPending && send.variables === true}
                      title={!testList.length ? "Önce test alıcısı gir" : undefined}
                      onClick={() => send.mutate(true)}
                    >
                      Test gönder
                    </Button>
                    <input
                      className="input mono !w-[92px] text-center"
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                      placeholder="kod"
                      aria-label="Doğrulama kodu (6 hane)"
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                    <Button
                      variant="primary"
                      disabled={!ready || totp.length !== 6 || send.isPending}
                      loading={send.isPending && send.variables === false}
                      onClick={() => send.mutate(false)}
                    >
                      {estimate.data ? `${estimate.data.count.toLocaleString("tr-TR")} kişiye gönder` : "Gönder"}
                    </Button>
                    <span className="text-2xs text-neutral-600">
                      toplu gönderim doğrulama uygulamasındaki kodu ister
                    </span>
                  </div>

                  {send.error && (
                    <p role="alert" className="text-xs text-danger">
                      {humanizeError(send.error, "Gönderilemedi")}
                    </p>
                  )}
                </div>
              </Card>

              {result && (
                <Card
                  title={result.failed ? "Gönderildi · hatalar var" : "Gönderildi"}
                  action={<Dot tone={result.failed ? "warn" : "ok"} />}
                >
                  <div className="text-sm text-neutral-300">
                    {result.sent} gönderildi · {result.failed} başarısız
                    {result.removedTokens > 0 && ` · ${result.removedTokens} artık kullanılmayan cihaz kaydı silindi`}
                  </div>
                  {result.errors.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs break-words text-danger">
                      {result.errors.map((e) => (
                        <li key={e.token}>{e.message}</li>
                      ))}
                    </ul>
                  )}
                </Card>
              )}
            </div>

            <div className="grid min-w-0 gap-4">
              <Card title="Kayıtlı cihazlar" flush>
                {stats.isError ? (
                  <ErrorState compact error={stats.error} onRetry={() => void stats.refetch()} />
                ) : stats.isLoading ? (
                  <Empty>Yükleniyor…</Empty>
                ) : stats.data?.length ? (
                  stats.data.map((row, i) => (
                    <div key={i} className="row py-[9px] text-xs">
                      <span className="min-w-0 text-neutral-300">
                        {row.platform}
                        {row.locale ? ` · ${row.locale}` : ""}
                        {row.appVersion ? ` · ${row.appVersion}` : ""}
                        {row.isPremium === true ? " · premium" : row.isPremium === false ? " · ücretsiz" : ""}
                      </span>
                      <span className="mono ml-auto text-neutral-100">{row.count}</span>
                    </div>
                  ))
                ) : (
                  <Empty>Henüz cihaz kaydı yok</Empty>
                )}
              </Card>

              <Card title="Geçmiş" flush>
                {campaigns.isError ? (
                  <ErrorState compact error={campaigns.error} onRetry={() => void campaigns.refetch()} />
                ) : campaigns.isLoading ? (
                  <Empty>Yükleniyor…</Empty>
                ) : campaigns.data?.length ? (
                  campaigns.data.map((c) => (
                    <div key={c.id} className="grid gap-1 border-b border-neutral-900 px-4 py-2.5 last:border-b-0">
                      <span className="text-sm break-words text-neutral-200">{c.title}</span>
                      <span className="text-2xs text-neutral-600">
                        {c.testOnly ? "test · " : ""}
                        {c.sentAt ? new Date(c.sentAt).toLocaleString("tr-TR") : "gönderilmedi"}
                        {c.sentCount != null && ` · ${c.sentCount} gönderildi`}
                      </span>
                    </div>
                  ))
                ) : (
                  <Empty>Henüz kampanya yok</Empty>
                )}
              </Card>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
